import { Config } from '@chegg-wt/core/config'
import fetch from 'isomorphic-unfetch'
import { JOURNAL, JSTOR } from '@chegg-wt/core/constants/journalTypes'
import { JournalType, Source } from '@chegg-wt/core/types/core'
import {
  isAutociteUrl,
  cslToEB,
  hasAutociteUrl,
  getSourceType,
  getAutociteUrl,
  SourceType,
  addWorldcatLinkMaybe,
  months,
  cslOneGraphToEb,
} from '@chegg-wt/core/utils/searchSources'
import { bff } from '@chegg-wt/core/api/bff'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import { v4 as uuidv4 } from 'uuid'
import { getCslDataFromDSApi } from '@chegg-wt/core/api/getCslDataFromDSApi'
import { AutociteCslDataInput } from '@chegg-wt/core/globalTypesOneGraph'
import { setNewRelicCustomAttribute } from '@chegg-wt/core/utils/newRelic'
import { CrossrefResultCountVariant, SourceEnum } from '../globalTypes'
import { Cookies, getCookie } from '../utils/cookies'
import { isDoiUrl } from '../components/CitationSearchInput/utils/isDoiUrl'
import { getClientCommonFields } from '../utils/plusCiteRioEvent'

export const autociteUrl = (url: string) =>
  fetch(`${Config.autociteUrl}/api/v3/query?url=${encodeURIComponent(url)}`, {
    method: 'get',
    credentials: 'include',
    headers: {
      Accept: 'application/vnd.com.easybib.data+json',
      Authorization: `Bearer ${Config.autociteKey}`,
      Pragma: 'no-cache',
      'X-AlCell': typeof window !== 'undefined' ? getCookie(Cookies.AdLayout) || '' : '',
    },
  }).then((res) =>
    Promise.all([res.ok, res.json()]).then(([ok, json]) =>
      ok ? json : Promise.reject(json)
    )
  )

const changeAccessDateToLocal = (sources: Source[]): Source[] => {
  // Expected only one result
  if (sources.length !== 1) return sources

  const currDate = new Date()
  return [
    {
      ...sources[0],
      data: {
        ...sources[0].data,
        pubonline: {
          ...sources[0].data.pubonline,
          dayaccessed: currDate.getDate().toString(),
          monthaccessed: months[currDate.getMonth()],
          yearaccessed: currDate.getFullYear().toString(),
        },
      },
    },
  ]
}
export const searchSource = (
  sourceType: string,
  query: string,
  crossrefResultCountEnabled: boolean
): Promise<Source[]> => {
  const type = sourceType.toLowerCase()

  switch (type) {
    case SourceType.website:
      setNewRelicCustomAttribute('bingSourceUsed', '1')
      return bff.searchWebsite({ keywords: query })
    case SourceType.article:
    case SourceType.journal:
      return bff.searchJournal({
        keywords: isDoiUrl(query) ? query : encodeURIComponent(query),
        crossrefResultCountVariant: crossrefResultCountEnabled
          ? CrossrefResultCountVariant.variation
          : CrossrefResultCountVariant.control,
      })
    case SourceType.book:
    default:
      return bff.searchWorldcat({
        keywords: query,
        sourceType: SourceEnum[type] || SourceEnum.book,
      })
  }
}

export interface SearchArgs {
  sourceType: string
  searchQuery: string
  autociteApiVersion: string
  clientCommonFields: {
    softLoggedIn: boolean
    cheggUserUuid?: string | null
  }
  journalType?: JournalType
  crossrefResultCountEnabled?: boolean
}

export const searchSources = async ({
  sourceType,
  searchQuery,
  autociteApiVersion,
  clientCommonFields,
  journalType = JOURNAL,
  crossrefResultCountEnabled = false,
}: SearchArgs): Promise<Source[]> => {
  try {
    if (sourceType === 'website') {
      setNewRelicCustomAttribute('websiteSourceUsed', '1')
    }
    if (isAutociteUrl(sourceType, searchQuery)) {
      const autociteRioTrackingID = uuidv4()
      let updatedResult: Source[]
      if (autociteApiVersion === 'v1') {
        const request: AutociteCslDataInput = { url: searchQuery }
        request.trackingId = autociteRioTrackingID
        request.clientCommonField = getClientCommonFields(
          clientCommonFields?.softLoggedIn,
          clientCommonFields?.cheggUserUuid
        )
        const result = await getCslDataFromDSApi(request)
        updatedResult = changeAccessDateToLocal(cslOneGraphToEb(result.data))
      } else {
        setNewRelicCustomAttribute('autociteSourceUsed', '1')
        const autociteResult = await autociteUrl(searchQuery)
        updatedResult = changeAccessDateToLocal(cslToEB(autociteResult))
      }
      if (updatedResult.length > 0) {
        updatedResult[0].autociteRioTrackingID = autociteRioTrackingID
      }
      return updatedResult
    }
  } catch (err) {
    // Nothing to do
  }
  if (sourceType === 'journal' && journalType === JSTOR) {
    return bff.searchJstor({ keywords: searchQuery })
  }
  const sources = await searchSource(sourceType, searchQuery, crossrefResultCountEnabled)
  return addWorldcatLinkMaybe(sourceType, sources)
}

export const getAutociteSource = async (
  source: Source,
  autociteApiVersion: string,
  clientCommonFields: {
    softLoggedIn: boolean
    cheggUserUuid?: string | null
  }
): Promise<Source> => {
  // check for autocite url
  if (hasAutociteUrl(source)) {
    const sourceType = getSourceType(source)
    const autociteUrl = getAutociteUrl(source)
    try {
      const sources = await searchSources({
        searchQuery: autociteUrl,
        sourceType,
        autociteApiVersion,
        clientCommonFields,
      })
      // We expect only one result returned
      const sourcesLength = sources.length
      if (sourcesLength === 1) {
        return sources[0]
      }
    } catch (e) {
      trackWithoutThrow(e)
    }
  }

  // return the original source if we can't get an autocite source
  return source
}
